import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Check, X, Copy, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const TEMPLATE_TYPES = [
  { value: 'single_dhoti', label: 'Single Item - Dhoti' },
  { value: 'single_belt', label: 'Single Item - Belt/Other' },
  { value: 'combo_2in1', label: '2-in-1 Combo' },
  { value: 'combo_3in1', label: '3-in-1 Combo' },
  { value: 'custom', label: 'Custom Template' }
];

const DEFAULT_FIELDS = {
  quality_label: { x: 16, y: 16, font_size: 10.5, bold: false, text: 'QUALITY' },
  quality_value: { x: 160, y: 16, font_size: 14.0, bold: false },
  content_label: { x: 16, y: 56, font_size: 10.5, bold: false, text: 'CONTENT' },
  content_value: { x: 160, y: 56, font_size: 14.0, bold: false },
  size_label: { x: 16, y: 96, font_size: 10.5, bold: false, text: 'SIZE' },
  size_value: { x: 160, y: 96, font_size: 10.5, bold: false },
  color_label: { x: 16, y: 136, font_size: 10.5, bold: false, text: 'COLOR' },
  color_value: { x: 160, y: 136, font_size: 10.5, bold: false },
  mrp_label: { x: 150, y: 150, font_size: 10.5, bold: false, text: 'MRP :' },
  mrp_value: { x: 220, y: 140, font_size: 18.0, bold: true },
  wsp_label: { x: 0, y: 0, font_size: 10.5, bold: false, text: 'WSP :' },
  wsp_value: { x: 0, y: 0, font_size: 18.0, bold: true },
  qty: { x: 16, y: 150, font_size: 10.5, bold: false, value: 1 },
  tax_notice: { x: 220, y: 180, font_size: 7.5, bold: false },
  made_in_india: { x: 16, y: 220, font_size: 10.5, bold: false },
  qr1: { x: 16, y: 280, size: 4 },
  qr1_text: { x: 16, y: 400, font_size: 10.5, bold: false },
  qr2: { x: 200, y: 280, size: 4 },
  qr2_text: { x: 200, y: 400, font_size: 10.5, bold: false },
  consumer_notice: { x: 16, y: 450, font_size: 7.5, bold: false, box: true }
};

const MainLabelTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'single_dhoti',
    label_width_dots: 800,
    label_height_dots: 640,
    dpi: 203,
    is_default: false,
    fields: { ...DEFAULT_FIELDS }
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        template_name: template.template_name,
        template_type: template.template_type,
        label_width_dots: template.label_width_dots,
        label_height_dots: template.label_height_dots,
        dpi: template.dpi || 203,
        is_default: template.is_default,
        fields: template.fields || { ...DEFAULT_FIELDS }
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        template_name: '',
        template_type: 'single_dhoti',
        label_width_dots: 800,
        label_height_dots: 640,
        dpi: 203,
        is_default: false,
        fields: { ...DEFAULT_FIELDS }
      });
    }
    setActiveTab('basic');
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingTemplate) {
        await apiClient.put(`/main-labels/templates/${editingTemplate.template_id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await apiClient.post('/main-labels/templates', formData);
        toast.success('Template created successfully');
      }
      
      setShowDialog(false);
      fetchTemplates();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save template';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await apiClient.post(`/main-labels/templates/${templateId}/set-default`);
      toast.success('Set as default template');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    
    try {
      await apiClient.delete(`/main-labels/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete template';
      toast.error(errorMsg);
    }
  };

  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setFormData({
      template_name: `${template.template_name} (Copy)`,
      template_type: template.template_type,
      label_width_dots: template.label_width_dots,
      label_height_dots: template.label_height_dots,
      dpi: template.dpi || 203,
      is_default: false,
      fields: JSON.parse(JSON.stringify(template.fields || DEFAULT_FIELDS))
    });
    setActiveTab('basic');
    setShowDialog(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldChange = (fieldKey, property, value) => {
    setFormData(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          [property]: value
        }
      }
    }));
  };

  const getWidthInMm = (dots) => (dots / 8).toFixed(1);
  const getHeightInMm = (dots) => (dots / 8).toFixed(1);

  const renderFieldEditor = (fieldKey, fieldLabel, isQr = false) => {
    const field = formData.fields[fieldKey] || {};
    
    return (
      <div key={fieldKey} className="border border-[#E5DFD0] rounded-lg p-3 space-y-3" data-testid={`field-editor-${fieldKey}`}>
        <h4 className="font-medium text-[#1A1F2E] text-sm">{fieldLabel}</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">X (dots)</Label>
            <Input
              type="number"
              value={field.x || 0}
              onChange={(e) => handleFieldChange(fieldKey, 'x', parseInt(e.target.value) || 0)}
              className="h-8 text-sm"
              data-testid={`field-${fieldKey}-x`}
            />
          </div>
          <div>
            <Label className="text-xs">Y (dots)</Label>
            <Input
              type="number"
              value={field.y || 0}
              onChange={(e) => handleFieldChange(fieldKey, 'y', parseInt(e.target.value) || 0)}
              className="h-8 text-sm"
              data-testid={`field-${fieldKey}-y`}
            />
          </div>
          {isQr ? (
            <div>
              <Label className="text-xs">QR Size</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={field.size || 4}
                onChange={(e) => handleFieldChange(fieldKey, 'size', parseInt(e.target.value) || 4)}
                className="h-8 text-sm"
              />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-xs">Font Size (Arial)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  max="50"
                  value={field.font_size || 10.5}
                  onChange={(e) => handleFieldChange(fieldKey, 'font_size', parseFloat(e.target.value) || 10.5)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`bold-${fieldKey}`}
                    checked={field.bold || false}
                    onCheckedChange={(checked) => handleFieldChange(fieldKey, 'bold', checked)}
                  />
                  <Label htmlFor={`bold-${fieldKey}`} className="text-xs cursor-pointer">Bold</Label>
                </div>
              </div>
            </>
          )}
        </div>
        {fieldKey === 'qty' && (
          <div>
            <Label className="text-xs">Quantity (N)</Label>
            <Input
              type="number"
              min="1"
              value={field.value ?? 1}
              onChange={(e) => handleFieldChange(fieldKey, 'value', parseInt(e.target.value) || 1)}
              className="h-8 text-sm"
              data-testid="qty-value-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">Prints as "QTY : {field.value ?? 1} N"</p>
          </div>
        )}
        {(fieldKey.endsWith('_label') || field.text !== undefined) && (
          <div>
            <Label className="text-xs">Label Text</Label>
            <Input
              value={field.text || ''}
              onChange={(e) => handleFieldChange(fieldKey, 'text', e.target.value)}
              className="h-8 text-sm"
              placeholder="Static label text"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Main Label Templates</h1>
              <p className="text-sm text-[#E8DCC8]">Configure product label layouts</p>
            </div>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="new-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-spinner">
            <div className="inline-block w-8 h-8 border-4 border-[#2D5F4F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : templates.length === 0 ? (
          <Card className="border-2 border-dashed border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Eye className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
              <p className="text-[#5A5F6E]">No templates created yet</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-[#2D5F4F] hover:bg-[#234A3D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="templates-grid">
            {templates.map((template) => (
              <Card 
                key={template.template_id} 
                className={`border-2 ${template.is_default ? 'border-[#2D5F4F] bg-[#2D5F4F]/5' : 'border-[#E5DFD0]'}`}
                data-testid={`template-card-${template.template_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="flex items-center gap-2 text-base truncate">
                        {template.template_name}
                        {template.is_default && (
                          <span className="text-xs bg-[#2D5F4F] text-white px-2 py-0.5 rounded shrink-0">Default</span>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1 text-xs">
                        {TEMPLATE_TYPES.find(t => t.value === template.template_type)?.label || template.template_type}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between text-[#5A5F6E]">
                      <span>Dimensions:</span>
                      <span className="font-medium text-[#1A1F2E]">
                        {getWidthInMm(template.label_width_dots)}mm × {getHeightInMm(template.label_height_dots)}mm
                      </span>
                    </div>
                    <div className="flex justify-between text-[#5A5F6E]">
                      <span>Resolution:</span>
                      <span className="font-medium text-[#1A1F2E]">{template.dpi || 203} DPI</span>
                    </div>
                    <div className="flex justify-between text-[#5A5F6E]">
                      <span>Fields:</span>
                      <span className="font-medium text-[#1A1F2E]">
                        {Object.keys(template.fields || {}).length} configured
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(template)}
                      className="flex-1 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                      data-testid={`edit-template-${template.template_id}`}
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(template)}
                      className="border-[#5A5F6E] text-[#5A5F6E] hover:bg-[#5A5F6E]/10"
                      data-testid={`duplicate-template-${template.template_id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(template.template_id, template.template_name)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      disabled={template.is_default}
                      data-testid={`delete-template-${template.template_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {!template.is_default && (
                    <Button
                      onClick={() => handleSetDefault(template.template_id)}
                      variant="ghost"
                      className="w-full mt-2 text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                      size="sm"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Set as Default
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="labels">Labels</TabsTrigger>
                <TabsTrigger value="values">Values</TabsTrigger>
                <TabsTrigger value="qr">QR Codes</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="template_name">Template Name *</Label>
                    <Input
                      id="template_name"
                      value={formData.template_name}
                      onChange={(e) => handleInputChange('template_name', e.target.value)}
                      placeholder="e.g., Premium Dhoti Label"
                      required
                      className="mt-1"
                      data-testid="template-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="template_type">Template Type *</Label>
                    <Select
                      value={formData.template_type}
                      onValueChange={(value) => handleInputChange('template_type', value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="template-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="dpi">DPI (Resolution)</Label>
                    <Select
                      value={String(formData.dpi)}
                      onValueChange={(value) => handleInputChange('dpi', parseInt(value))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="203">203 DPI (Standard)</SelectItem>
                        <SelectItem value="300">300 DPI (High)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-2 border-[#E5DFD0] rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-[#1A1F2E]">Label Dimensions</h3>
                  <p className="text-xs text-[#5A5F6E]">At 203 DPI: 8 dots = 1mm</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="label_width_dots">Width (dots) *</Label>
                      <Input
                        id="label_width_dots"
                        type="number"
                        value={formData.label_width_dots}
                        onChange={(e) => handleInputChange('label_width_dots', parseInt(e.target.value) || 0)}
                        required
                        className="mt-1"
                      />
                      <p className="text-xs text-[#5A5F6E] mt-1">≈ {getWidthInMm(formData.label_width_dots)}mm</p>
                    </div>
                    <div>
                      <Label htmlFor="label_height_dots">Height (dots) *</Label>
                      <Input
                        id="label_height_dots"
                        type="number"
                        value={formData.label_height_dots}
                        onChange={(e) => handleInputChange('label_height_dots', parseInt(e.target.value) || 0)}
                        required
                        className="mt-1"
                      />
                      <p className="text-xs text-[#5A5F6E] mt-1">≈ {getHeightInMm(formData.label_height_dots)}mm</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_default"
                    checked={formData.is_default}
                    onCheckedChange={(checked) => handleInputChange('is_default', checked)}
                  />
                  <Label htmlFor="is_default" className="cursor-pointer">
                    Set as default template
                  </Label>
                </div>
              </TabsContent>

              <TabsContent value="labels" className="space-y-3">
                <p className="text-sm text-[#5A5F6E] mb-4">Configure static label positions (e.g., "QUALITY", "SIZE", "MRP")</p>
                {renderFieldEditor('quality_label', 'Quality Label')}
                {renderFieldEditor('content_label', 'Content Label')}
                {renderFieldEditor('size_label', 'Size Label')}
                {renderFieldEditor('color_label', 'Color Label')}
                {renderFieldEditor('mrp_label', 'MRP Label')}
                {renderFieldEditor('wsp_label', 'WSP Label')}
              </TabsContent>

              <TabsContent value="values" className="space-y-3">
                <p className="text-sm text-[#5A5F6E] mb-4">Configure dynamic value positions (populated from data)</p>
                {renderFieldEditor('quality_value', 'Quality Value')}
                {renderFieldEditor('content_value', 'Content Value (Single)')}
                {renderFieldEditor('content1', 'Content 1 (Combo)')}
                {renderFieldEditor('content2', 'Content 2 (Combo)')}
                {renderFieldEditor('content3', 'Content 3 (3-in-1)')}
                {renderFieldEditor('size_value', 'Size Value (Single)')}
                {renderFieldEditor('size1', 'Size 1 (Combo)')}
                {renderFieldEditor('size2', 'Size 2 (Combo)')}
                {renderFieldEditor('size3', 'Size 3 (3-in-1)')}
                {renderFieldEditor('color_value', 'Color Value')}
                {renderFieldEditor('mrp_value', 'MRP Value')}
                {renderFieldEditor('wsp_value', 'WSP Value')}
                {renderFieldEditor('qty', 'Quantity')}
                {renderFieldEditor('tax_notice', 'Tax Notice')}
                {renderFieldEditor('made_in_india', 'Made in India')}
                {renderFieldEditor('desc5_bold', 'Description 5 (Bold)')}
                {renderFieldEditor('consumer_notice', 'Consumer Notice')}
              </TabsContent>

              <TabsContent value="qr" className="space-y-3">
                <p className="text-sm text-[#5A5F6E] mb-4">Configure QR code positions</p>
                {renderFieldEditor('qr1', 'QR Code 1 (USN)', true)}
                {renderFieldEditor('qr1_text', 'QR 1 Text (USN Code)')}
                {renderFieldEditor('qr2', 'QR Code 2 (Optional Barcode)', true)}
                {renderFieldEditor('qr2_text', 'QR 2 Text')}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={isSubmitting}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                disabled={isSubmitting}
                data-testid="save-template-btn"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainLabelTemplates;
